import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { getPostLoginRedirect } from '../utils/rbac';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { GlassCard } from '../components/ui';
import { Eye, EyeOff, Mail, Lock, Brain, Shield } from 'lucide-react';
import Iridescence from '../components/Iridescence';
import toast from 'react-hot-toast';

// Memoize Iridescence to prevent re-renders
const MemoizedIridescence = React.memo(Iridescence);

const Login = () => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  
  // Debug: Check if login function exists
  useEffect(() => {
    console.log('🔍 Login: useAuth returned:', { hasLogin: typeof login, hasUser: !!user });
  }, [login, user]);

  // Memoize handlers to prevent re-renders
  const handleEmailChange = useMemo(() => (e) => {
    setCredentials(prev => ({ ...prev, email: e.target.value }));
  }, []);

  const handlePasswordChange = useMemo(() => (e) => {
    setCredentials(prev => ({ ...prev, password: e.target.value }));
  }, []);

  const togglePasswordVisibility = useMemo(() => () => {
    setShowPassword(prev => !prev);
  }, []);

  // Remove automatic redirect - let users manually navigate after login

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!credentials.email || !credentials.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      console.log('🔑 Login: Attempting login for:', credentials.email);
      console.log('🔍 Login: login function type:', typeof login);
      
      if (typeof login !== 'function') {
        throw new Error('login is not a function - AuthContext may not be properly initialized');
      }
      
      const result = await login(credentials.email, credentials.password);

      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('redirect')) {
        return;
      }
      
      // Force token refresh to get latest custom claims
      console.log('🔄 Login: Forcing token refresh to get custom claims...');
      const tokenResult = await result.user.getIdTokenResult(true); // true = force refresh
      const userRole = tokenResult.claims.role || 'user';
      console.log('🔄 Login: Token refreshed, role:', userRole);
      
      // Wait for auth context to update
      setTimeout(() => {
        console.log('🔄 Login: Redirecting based on role:', userRole);
        
        // Simple role-based redirect
        switch (userRole) {
          case 'admin':
            navigate('/admin/dashboard');
            break;
          case 'customer_admin':
            navigate('/customer/dashboard');
            break;
          case 'hr':
            navigate('/hr/dashboard');
            break;
          default:
            navigate('/assessment');
        }
      }, 500);
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen relative overflow-hidden" style={{ touchAction: 'pan-y' }}>
      {/* Fixed Iridescence Background - Memoized to prevent re-renders */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <MemoizedIridescence 
          color={[0.18, 0.16, 0.31]}
          mouseReact={true}
          amplitude={0.15}
          speed={0.8}
        />
      </div>
      
      {/* Fixed Blur Overlay */}
      <div className="fixed inset-0 backdrop-blur-sm z-10 pointer-events-none" />
      
      {/* Scrollable Content */}
      <div className="relative h-full overflow-y-auto z-20 scrollbar-hide">
        {/* Content Container */}
        <div className="flex items-center justify-center min-h-screen p-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-md"
          >
        <div className="text-center mb-3">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ 
              delay: 0.2, 
              type: "spring", 
              stiffness: 200,
              damping: 15
            }}
            whileHover={{ scale: 1.05, rotate: 5 }}
            className="mx-auto mb-4"
          >
            <img src="/splash-logo.webp" alt="CANDIGATE" className="w-52 h-52 mx-auto drop-shadow-2xl object-contain" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl font-display font-bold text-white mb-2"
          >
            Welcome Back
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-indigo-100 text-lg"
          >
            Sign in to continue your assessment journey
          </motion.p>
        </div>

        <GlassCard className="shadow-2xl border-0">
          <CardHeader className="space-y-1 pb-2">
            <CardTitle className="text-2xl font-semibold text-center">Sign In</CardTitle>
            <CardDescription className="text-center">
              Enter your credentials to access your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="email"
                    type="email"
                    value={credentials.email}
                    onChange={handleEmailChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-berry focus:border-transparent transition-all duration-200 bg-white/50"
                    placeholder="Enter your email"
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={credentials.password}
                    onChange={handlePasswordChange}
                    className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-berry focus:border-transparent transition-all duration-200 bg-white/50"
                    placeholder="Enter your password"
                    disabled={loading}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  type="submit"
                  loading={loading}
                  className="w-full py-3 bg-gradient-to-r from-brand-berry to-brand-rosewood hover:from-brand-rosewood hover:to-brand-berry text-white font-medium rounded-lg transition-smooth shadow-lg hover:shadow-2xl"
                  size="lg"
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
              </motion.div>
            </form>
          </CardContent>
        </GlassCard>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Login;
